"use client";

import { useState } from "react";
import { IconCircleCheckOutline18 } from "nucleo-ui-outline-18";

import type { SubTopic } from "@/lib/marketing/subTopic";
import { cn } from "@/lib/utils";

type SubTopicSplitContentProps = {
  subTopics: SubTopic[];
};

/**
 * Numbered sub-topic nav (left) + Challenge / How We Solve It panel (right).
 * All panels are SSR'd and non-active ones hidden via CSS so crawlers index
 * every sub-topic. State-resets on parent change rely on a `key` from the
 * caller — see `MarketingDetailLayout`.
 */
export function SubTopicSplitContent({ subTopics }: SubTopicSplitContentProps) {
  const [activeSlug, setActiveSlug] = useState(subTopics[0]?.slug);

  if (subTopics.length === 0) return null;

  return (
    <section className="px-6 py-16 md:py-24">
      <div className="mx-auto grid max-w-[1280px] gap-12 lg:grid-cols-[minmax(260px,360px)_1fr] lg:gap-20">
        <nav aria-label="Sub-topics" className="lg:sticky lg:top-24 lg:self-start">
          <ol className="flex flex-col gap-4">
            {subTopics.map((topic, i) => {
              const isActive = topic.slug === activeSlug;
              const num = String(i + 1).padStart(2, "0");
              return (
                <li key={topic.slug}>
                  <button
                    type="button"
                    onClick={() => setActiveSlug(topic.slug)}
                    aria-current={isActive ? "true" : undefined}
                    className={cn(
                      "flex w-full items-center gap-3 text-left font-mono text-sm uppercase tracking-wider transition-colors",
                      isActive ? "text-ds-text-primary" : "text-ds-text-muted hover:text-ds-text-secondary",
                    )}
                  >
                    <span className={cn("shrink-0 font-mono", isActive ? "text-ds-brand-600" : "text-ds-text-muted")}>
                      [{num}]
                    </span>
                    <span>{topic.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="relative">
          {subTopics.map(topic => {
            const isActive = topic.slug === activeSlug;
            return (
              <article
                key={topic.slug}
                aria-hidden={!isActive}
                className={cn("flex flex-col gap-10", isActive ? "block" : "hidden")}
              >
                <div>
                  <p className="text-ds-brand-600 mb-3 font-mono text-xs font-medium uppercase tracking-wider">
                    The Challenge
                  </p>
                  <h3 className="text-ds-text-primary mb-4 text-2xl font-medium md:text-3xl">{topic.title}</h3>
                  <p className="text-ds-text-subtle text-base leading-7 md:text-lg">{topic.challenge}</p>
                </div>

                <div className="border-ds-neutral-200 border-t pt-10">
                  <p className="text-ds-brand-600 mb-3 font-mono text-xs font-medium uppercase tracking-wider">
                    How We Solve It
                  </p>
                  <p className="text-ds-text-subtle mb-6 text-base leading-7 md:text-lg">{topic.solutionIntro}</p>
                  <ul className="flex flex-col gap-3">
                    {topic.solutionPoints.map((point, j) => (
                      <li key={`${topic.slug}-${j}`} className="flex items-start gap-3">
                        <IconCircleCheckOutline18 className="text-ds-brand-500 mt-0.5 shrink-0" />
                        <span className="text-ds-text-subtle text-base leading-7">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
